// Which executions will ACTUALLY share a drum — and which the planner thinks
// will, but won't.
//
// Reported 2026-10-23: three CYYF 3x2.5 lots launched together with Merge
// Execution on the rope, one more drum wanted out of the ~5 000 m left on each.
// "pentru 3 PRODUSE LANSATE IN MERGE EXECUTION NU IMI FUNCTIONEAZA imi ia doar
// 2." Two of the three merged. The third did not, and nothing said so.
//
// The reason is in `buildMergedOutputKey`: for a rope the base key is
//
//     MERGE::rope::<machineId>::<drum_material>::<drum_diameter>
//
// Two executions share a drum only when that matches AND their slot matches. The
// first two products had their Quick drum preset set (METAL / 1250) and the
// third did not — "Select drum material first." — so its key was
// `MERGE::rope::DTB1250::::` and it met nobody. The preset is per product, the
// toggle that says "merge these" is per phase, and the gap between the two was
// silent.
//
// Which is a fair thing for the key to do: one drum has one material and one
// diameter, so two ropes with different drum specs are not one drum. The defect
// is that it happened without a word. This computes, from the SAME functions the
// launch uses, who each execution ends up with — so the phase step can say
// "shares a drum with 2 other products" or "will be ALONE, because …" while
// there is still time to fix it.
//
// Pure. Mirrored into tests/.generated by tests/tools/mirror.mjs.

// Extensions spelled out: tests/tools/mirror.mjs runs these files through Node
// directly and will not guess them.
import {
  buildMergedOutputKey,
  isPhaseMerged,
  planMergeSlots
} from './mergeSlot.js';
import { executionsOf, productIdentityOf } from './launchPreview.js';

/** The parts of a merge key a human can act on, per phase. */
const KEY_PARTS = {
  rope: ['machineId', 'drum_material', 'drum_diameter'],
  unwinding: ['wireDiameter'],
  conductor_isolation: ['color']
};
const DEFAULT_PARTS = [
  'machineId',
  'drum_material',
  'drum_diameter',
  'deliveryMode',
  'color'
];

const LABELS = {
  machineId: 'machine',
  drum_material: 'drum material',
  drum_diameter: 'drum Ø',
  deliveryMode: 'delivery',
  color: 'colour',
  wireDiameter: 'wire Ø'
};

const blank = v => v == null || String(v).trim() === '';

/**
 * Who shares a drum with whom, across every product in one submission.
 *
 * @param products     standardData
 * @param phaseName    the phase being configured
 * @param mergedPhases the wizard's per-phase toggles
 * @param labelOf      product -> something to call it
 * @returns Map keyed `${productIdentity}#${execIndex}` ->
 *          { slot, baseKey, partners: [{ label }], missing: [field], mine: {field: value} }
 *          `partners` is the OTHER products on this drum; empty means alone.
 */
export function mergeRealityForPhase({
  products = [],
  phaseName,
  mergedPhases = {},
  labelOf = p => p?.productLabel || p?.name || '—'
} = {}) {
  const out = new Map();
  if (!isPhaseMerged(mergedPhases, phaseName)) return out;

  const parts = KEY_PARTS[phaseName] || DEFAULT_PARTS;
  const rows = [];

  // Slots are planned PER PRODUCT, exactly as the launch does it: each product
  // numbers its own executions, and two products independently landing on the
  // same base key and slot is what pairs them.
  (products || []).forEach((product, pi) => {
    const phase = (product?.phases || []).find(p => p?.name === phaseName);
    if (!phase) return;

    const execs = executionsOf(phase);
    if (!execs.length) return;

    const identity = productIdentityOf(product, pi);
    const items = execs.map(({ exec, color }, i) => ({
      id: `${identity}#${i}`,
      jobKey: identity,
      baseKey: buildMergedOutputKey({ phaseName, exec: { ...exec, color } }),
      mergeSlot: exec?.mergeSlot,
      mergeSolo: !!exec?.mergeSolo
    }));

    let slots;
    try {
      slots = planMergeSlots(items);
    } catch {
      // Two executions of one product on one slot — the launch will refuse it
      // with MergeSlotConflict and say so there. Nothing to describe here.
      return;
    }

    execs.forEach(({ exec, color }, i) => {
      const item = items[i];
      rows.push({
        key: item.id,
        identity,
        label: labelOf(product, pi),
        baseKey: item.baseKey,
        slot: slots.get(item.id) ?? 0,
        solo: !!exec?.mergeSolo,
        mine: Object.fromEntries(
          parts.map(f => [f, f === 'color' ? color : exec?.[f]])
        )
      });
    });
  });

  for (const row of rows) {
    const partners = rows.filter(
      other =>
        other !== row &&
        other.identity !== row.identity &&
        other.baseKey === row.baseKey &&
        other.slot === row.slot
    );

    out.set(row.key, {
      slot: row.slot,
      baseKey: row.baseKey,
      solo: row.solo,
      partners: partners.map(p => ({ label: p.label, identity: p.identity })),
      // Fields of the key that are not filled in. A blank one cannot match a
      // filled one, so it is the usual reason a product ends up on its own —
      // and the only reason that is a mistake rather than a choice.
      missing: Object.entries(row.mine)
        .filter(([, v]) => blank(v))
        .map(([f]) => f),
      mine: row.mine
    });
  }

  return out;
}

/**
 * The same thing in one sentence, for the card.
 *
 * Returns null when there is nothing worth saying — a phase that does not merge,
 * or a single-product submission.
 */
export function mergeNotice(entry, { productCount = 1 } = {}) {
  if (!entry || productCount < 2) return null;

  if (entry.solo) {
    return { tone: 'secondary', text: 'Alone — never merge (chosen)' };
  }

  if (entry.partners.length) {
    const names = entry.partners.map(p => p.label).join(', ');
    return {
      tone: 'success',
      text:
        `Shares a drum with ${entry.partners.length} other ` +
        `${entry.partners.length === 1 ? 'product' : 'products'} — ${names}` +
        ` (group ${entry.slot + 1})`
    };
  }

  if (entry.missing.length) {
    const what = entry.missing.map(f => LABELS[f] || f).join(' + ');
    return {
      tone: 'warning',
      text:
        `Will NOT merge — ${what} not set, so this drum matches no other ` +
        `product. Merging needs the same ${what}.`
    };
  }

  const stated = Object.entries(entry.mine)
    .filter(([, v]) => !blank(v))
    .map(([f, v]) => `${LABELS[f] || f} ${v}`)
    .join(', ');
  return {
    tone: 'warning',
    text: `Will NOT merge — no other product is planned on ${stated || 'this drum'}.`
  };
}
